import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

const ltlItemSchema = z.object({
	id: z.number(),
	description: z.string().nullable(),
	unitTypeId: z.number(),
	unitTypeName: z.string().nullable(),
	quantity: z.number(),
	weight: z.number(),
	weightUomId: z.number(),
	length: z.number(),
	width: z.number(),
	height: z.number(),
	dimensionUomId: z.number(),
	freightClassCode: z.string().nullable(),
	nmfcNumber: z.string().nullable(),
	isStackable: z.boolean().nullable(),
	isHazmat: z.boolean().nullable(),
});

const carrierServiceSchema = z.object({
	carrierName: z.string().nullable(),
	proNumber: z.string().nullable(),
	carrierQuoteId: z.string().nullable(),
	carrierQuoteExpirationDate: z.string().nullable(),
	agentProNumber: z.string().nullable(),
	tqlQuoteId: z.number().nullable(),
	serviceLevel: z.string().nullable(),
	serviceLevelAbbreviation: z.string().nullable(),
	transitDays: z.number().nullable(),
	serviceType: z.string().nullable(),
});

const additionalDetailsSchema = z.object({
	emergencyContactName: z.string().nullable(),
	emergencyContactNumber: z.string().nullable(),
	customerReference: z.string().nullable(),
	soNumber: z.string().nullable(),
	poNumber: z.number().nullable(),
	loadValue: z.number().nullable(),
	hazmat: z.boolean().nullable(),
	shipmentType: z.number().nullable(),
	accessorial: z.string().nullable(),
	specialInstructions: z.string().nullable().optional(),
	weight: z.number().nullable().optional(),
	customerPO: z.string().nullable().optional(),
	billRate: z.number().nullable().optional(),
});

const flyoutDetailsSchema = z.object({
	poNumber: z.number(),
	ltlItems: ltlItemSchema.array().nullable(),
	carrierService: carrierServiceSchema.optional(),
	additionalDetails: additionalDetailsSchema.optional(),
});

export type LtlItemType = z.infer<typeof ltlItemSchema>;
export type CarrierService = z.infer<typeof carrierServiceSchema>;
export type AdditionalDetails = z.infer<typeof additionalDetailsSchema>;
export type FlyoutDetails = z.infer<typeof flyoutDetailsSchema>;

async function fetchShipmentFlyoutDetails(
	poNumber: number | undefined | null,
): Promise<FlyoutDetails> {
	if (poNumber == null) {
		throw new Error("poNumber is required");
	}

	const result = await getAndValidate(
		`shipment-details/${poNumber}/flyout`,
		flyoutDetailsSchema,
	);

	if (!result.success) {
		throw new Error(result.error);
	}

	return result.data;
}

export function useShipmentFlyout(
	poNumber: number | undefined | null,
	isEnabled = true,
) {
	const { data, error, isLoading, isFetching } = useQuery({
		queryFn: () => fetchShipmentFlyoutDetails(poNumber),
		queryKey: ["shipmentDetails", poNumber],
		enabled: isEnabled && poNumber != null,
	});

	return {
		data,
		error,
		isLoading,
		isFetching,
	};
}
